import { Fragment, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ExternalLink, Search } from 'lucide-react';
import { api, type GatewayCommandRecord, type GatewayCommandStatus } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { CommandName, HistoryItem, StatusBadge } from '../components/GatewayCommandView';
import { LoadingTicker } from '../components/LoadingTicker';
import { formatExact, formatNumber, formatRelative, formatUtc } from '../lib/format';
import { cn } from '../lib/utils';

const RANGES = [
  { days: 1, label: '24 hours' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
] as const;

/** Results grouped the way an operator asks about them, not by every internal status. */
type ResultFilter = 'all' | 'ok' | 'waiting' | 'no-reply' | 'failed';
const RESULT_OF: Record<GatewayCommandStatus, Exclude<ResultFilter, 'all'>> = {
  done: 'ok',
  replied: 'ok',
  sending: 'waiting',
  pending: 'waiting',
  sent: 'waiting',
  'no-reply': 'no-reply',
  failed: 'failed',
};

const ANY = '';

/**
 * Every command sent to any gateway: who, what, when, why, over which channel,
 * and what came back. The table is the scan; a row opens the full record.
 */
export function GatewayHistory() {
  const [days, setDays] = useState<number>(30);
  const [q, setQ] = useState('');
  const [gateway, setGateway] = useState(ANY);
  const [actor, setActor] = useState(ANY);
  const [command, setCommand] = useState(ANY);
  const [via, setVia] = useState(ANY);
  const [result, setResult] = useState<ResultFilter>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['gateways', 'log', days],
    queryFn: () => api.listRecentGatewayCommands(days),
    staleTime: 30_000,
  });
  const items = useMemo(() => query.data?.items ?? [], [query.data]);

  // Pickers offer only what actually occurs in the range.
  const options = useMemo(() => {
    const uniq = (values: string[]) => [...new Set(values)].sort((a, b) => a.localeCompare(b));
    return {
      gateways: uniq(items.map((c) => c.routerName)),
      actors: uniq(items.map((c) => c.actor)),
      commands: uniq(items.map((c) => c.label)),
    };
  }, [items]);

  const needle = q.trim().toLowerCase();
  const rows = useMemo(
    () =>
      items.filter(
        (c) =>
          (gateway === ANY || c.routerName === gateway) &&
          (actor === ANY || c.actor === actor) &&
          (command === ANY || c.label === command) &&
          (via === ANY || c.channel === via) &&
          (result === 'all' || RESULT_OF[c.status] === result) &&
          (!needle ||
            [c.routerName, c.customer, c.actor, c.label, c.reason, c.simName, c.serial]
              .some((v) => v?.toLowerCase().includes(needle))),
      ),
    [items, gateway, actor, command, via, result, needle],
  );
  const filtered = gateway !== ANY || actor !== ANY || command !== ANY || via !== ANY || result !== 'all' || needle !== '';
  const keyOf = (c: GatewayCommandRecord) => `${c.routerId}/${c.sentAt}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Command History</h1>
          <p className="text-sm text-muted-foreground">
            Every command sent to a gateway — who sent it, what, when, why, and what came back.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Sent in the last
          <Select className="w-32" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            {RANGES.map((r) => (
              <option key={r.days} value={r.days}>
                {r.label}
              </option>
            ))}
          </Select>
        </label>
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
          <div className="relative w-64">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search gateway, customer, who, reason…"
              className="pl-9"
            />
          </div>
          <Picker label="Gateway" value={gateway} onChange={setGateway} options={options.gateways} />
          <Picker label="Sent by" value={actor} onChange={setActor} options={options.actors} />
          <Picker label="Command" value={command} onChange={setCommand} options={options.commands} />
          <Select className="w-28" value={via} onChange={(e) => setVia(e.target.value)} aria-label="Via">
            <option value={ANY}>Any channel</option>
            <option value="rms">RMS</option>
            <option value="sms">SMS</option>
          </Select>
          <Select
            className="w-32"
            value={result}
            onChange={(e) => setResult(e.target.value as ResultFilter)}
            aria-label="Result"
          >
            <option value="all">Any result</option>
            <option value="ok">Answered</option>
            <option value="waiting">Waiting</option>
            <option value="no-reply">No reply</option>
            <option value="failed">Failed</option>
          </Select>
          {filtered && (
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              onClick={() => {
                setQ('');
                setGateway(ANY);
                setActor(ANY);
                setCommand(ANY);
                setVia(ANY);
                setResult('all');
              }}
            >
              Clear filters
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {query.data &&
              (filtered
                ? `${formatNumber(rows.length)} of ${formatNumber(items.length)}`
                : `${formatNumber(items.length)} ${items.length === 1 ? 'command' : 'commands'}`)}
          </span>
        </div>

        {query.data?.truncated && (
          <p className="border-b border-line-soft bg-caution/5 px-4 py-2 text-xs text-caution">
            Showing the newest {formatNumber(items.length)} commands; older ones in this range were not loaded. Narrow
            the range to see them.
          </p>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-muted/50">
              <tr>
                {['', 'Sent', 'Gateway', 'Command', 'Via', 'Sent by', 'Reason', 'Result'].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-medium text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center">
                    <LoadingTicker active steps={['Reading the command history…']} />
                  </td>
                </tr>
              )}
              {query.error && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-destructive">
                    {(query.error as Error).message}
                  </td>
                </tr>
              )}
              {rows.map((c) => {
                const key = keyOf(c);
                const open = expanded === key;
                return (
                  <Fragment key={key}>
                    <tr
                      onClick={() => setExpanded(open ? null : key)}
                      className={cn(
                        'cursor-pointer border-b border-line-soft align-top hover:bg-raise',
                        open && 'bg-raise',
                        c.effect === 'write' && 'shadow-[inset_2px_0_0_var(--color-caution)]',
                      )}
                    >
                      <td className="py-2 pl-3 text-muted-foreground">
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap" title={formatUtc(c.timestamp)}>
                        <div>{formatExact(c.timestamp)}</div>
                        <div className="text-muted-foreground">{formatRelative(c.timestamp)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{c.routerName}</div>
                        <div className="text-xs text-muted-foreground">{c.customer ?? ''}</div>
                      </td>
                      <td className="px-3 py-2">
                        <CommandName record={c} />
                      </td>
                      <td className="px-3 py-2 text-xs font-medium">{c.channel === 'sms' ? 'SMS' : 'RMS'}</td>
                      <td className="px-3 py-2 text-xs">{c.actor}</td>
                      <td className="max-w-72 px-3 py-2 text-xs break-words">
                        {c.reason ?? <span className="text-muted-foreground">None given</span>}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge record={c} />
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-b border-line-soft bg-raise/50">
                        <td />
                        <td colSpan={7} className="px-3 pt-1 pb-3">
                          <ul className="max-w-3xl">
                            <HistoryItem record={c} />
                          </ul>
                          <RouterLink
                            to={`/gateways?gateway=${encodeURIComponent(c.routerId)}`}
                            className="mt-2 inline-flex items-center gap-1 text-xs text-accent hover:underline"
                          >
                            Open {c.routerName} <ExternalLink className="h-3 w-3" />
                          </RouterLink>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {query.data && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">
                    {filtered
                      ? 'No commands match these filters.'
                      : `No commands have been sent to any gateway in the last ${RANGES.find((r) => r.days === days)?.label ?? `${days} days`}.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Picker({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <Select className="w-44" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      <option value={ANY}>Any {label.toLowerCase()}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </Select>
  );
}
